import classNames from "classnames";
import React, { FC, useMemo, useState } from "react";
import {
  LuChevronDown,
  LuChevronRight,
  LuFolderPlus,
  LuHome,
} from "react-icons/lu";

import { useCreateFolderMutation } from "../../slices/DocumentsSlice";
import { FolderItem } from "./files.types";
import { buildTree, folderChain, FolderNode } from "./files.utils";
import {
  ErrorText,
  errorText,
  FilesModal,
  FolderGlyph,
  GHOST_BUTTON,
  INPUT,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  VisibilityPill,
} from "./ui/FilesUi";

/** Folders as options of a plain dropdown, indented by level. */
export const FolderSelect: FC<{
  id: string;
  folders: FolderItem[];
  value: string | null;
  onChange: (folderId: string | null) => void;
  topLabel?: string;
}> = ({ id, folders, value, onChange, topLabel = "Top level (no folder)" }) => {
  const options = useMemo(() => {
    const list: { id: string; label: string }[] = [];
    const walk = (nodes: FolderNode[], depth: number) =>
      nodes.forEach((n) => {
        list.push({ id: n.id, label: `${"   ".repeat(depth)}${n.name}` });
        walk(n.children, depth + 1);
      });
    walk(buildTree(folders), 0);
    return list;
  }, [folders]);

  return (
    <select
      id={id}
      className={INPUT}
      value={value || ""}
      onChange={(event) => onChange(event.target.value || null)}>
      <option value={""}>{topLabel}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  );
};

/** A clickable folder tree to choose where files go. */
const PickerTree: FC<{
  nodes: FolderNode[];
  depth: number;
  value: string | null;
  disabledIds: Set<string>;
  open: Set<string>;
  toggle: (id: string) => void;
  onPick: (id: string) => void;
}> = ({ nodes, depth, value, disabledIds, open, toggle, onPick }) => (
  <>
    {nodes.map((node) => {
      const isOpen = open.has(node.id);
      const disabled = disabledIds.has(node.id);
      return (
        <React.Fragment key={node.id}>
          <div
            className={classNames(
              "flex items-center gap-1.5 rounded-lg py-1.5 pr-2",
              value === node.id
                ? "bg-[#EFEBFF] text-[#4B32D6]"
                : "hover:bg-[#F7F8FA]",
              disabled && "opacity-40",
            )}
            style={{ paddingLeft: 6 + depth * 18 }}>
            <button
              type={"button"}
              aria-label={isOpen ? `Close ${node.name}` : `Open ${node.name}`}
              onClick={() => toggle(node.id)}
              className={classNames(
                "flex h-5 w-5 items-center justify-center text-[#667085]",
                node.children.length === 0 && "invisible",
              )}>
              {isOpen ? (
                <LuChevronDown size={14} />
              ) : (
                <LuChevronRight size={14} />
              )}
            </button>
            <button
              type={"button"}
              disabled={disabled}
              onClick={() => onPick(node.id)}
              className={
                "flex min-w-0 flex-1 items-center gap-2 text-left text-[13px] font-semibold disabled:cursor-not-allowed"
              }>
              <FolderGlyph className={"text-[#6B4EFF]"} />
              <span className={"truncate"}>{node.name}</span>
              {node.visibility !== "inherit" && node.effective !== "private" && (
                <span className={"ml-auto"}>
                  <VisibilityPill visibility={node.effective} compact={true} />
                </span>
              )}
            </button>
          </div>
          {isOpen && (
            <PickerTree
              nodes={node.children}
              depth={depth + 1}
              value={value}
              disabledIds={disabledIds}
              open={open}
              toggle={toggle}
              onPick={onPick}
            />
          )}
        </React.Fragment>
      );
    })}
  </>
);

/**
 * "Move to…" / "File to…": choose a folder of the client (or the top level),
 * with "New folder" right there.
 */
export const FolderPickerModal: FC<{
  title: string;
  subtitle?: string;
  customerId: string;
  folders: FolderItem[];
  initial?: string | null;
  confirmLabel?: string;
  allowTop?: boolean;
  disabledIds?: Set<string>;
  busy?: boolean;
  error?: string;
  onClose: () => void;
  onPick: (folderId: string | null) => void;
}> = ({
  title,
  subtitle,
  customerId,
  folders,
  initial = null,
  confirmLabel = "Move here",
  allowTop = true,
  disabledIds = new Set<string>(),
  busy,
  error,
  onClose,
  onPick,
}) => {
  const [value, setValue] = useState<string | null>(initial);
  const [open, setOpen] = useState<Set<string>>(() => {
    const start = new Set<string>();
    folders.filter((f) => !f.parentId).forEach((f) => start.add(f.id));
    // Show the current folder and what's inside it.
    folderChain(folders, initial).forEach((f) => start.add(f.id));
    return start;
  });
  const [newName, setNewName] = useState<string | null>(null);
  const [createError, setCreateError] = useState("");
  const [createFolder, { isLoading: creating }] = useCreateFolderMutation();
  const tree = useMemo(() => buildTree(folders), [folders]);

  const toggle = (id: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const create = async () => {
    if (!newName?.trim()) return;
    setCreateError("");
    try {
      const folder = await createFolder({
        customerId,
        parentId: value,
        name: newName.trim(),
      }).unwrap();
      setNewName(null);
      if (value) setOpen((current) => new Set(current).add(value));
      setValue(folder.id);
    } catch (e) {
      setCreateError(errorText(e));
    }
  };

  const chosen = folders.find((f) => f.id === value);

  return (
    <FilesModal
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button
            type={"button"}
            className={SECONDARY_BUTTON}
            onClick={onClose}
            disabled={busy}>
            Cancel
          </button>
          <button
            type={"button"}
            className={PRIMARY_BUTTON}
            disabled={busy || (!allowTop && !value)}
            onClick={() => onPick(value)}>
            {busy ? "Saving…" : confirmLabel}
          </button>
        </>
      }>
      <div className={"rounded-xl border border-[#EDEFF3] p-1.5"}>
        {allowTop && (
          <button
            type={"button"}
            onClick={() => setValue(null)}
            className={classNames(
              "flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] font-semibold",
              value === null
                ? "bg-[#EFEBFF] text-[#4B32D6]"
                : "hover:bg-[#F7F8FA]",
            )}>
            <LuHome size={15} className={"text-[#6B4EFF]"} />
            Top level (no folder)
          </button>
        )}
        <div className={"max-h-[320px] overflow-y-auto"}>
          <PickerTree
            nodes={tree}
            depth={0}
            value={value}
            disabledIds={disabledIds}
            open={open}
            toggle={toggle}
            onPick={setValue}
          />
          {tree.length === 0 && (
            <div className={"px-3 py-4 text-center text-[#667085]"}>
              This client has no folders yet.
            </div>
          )}
        </div>
      </div>
      <div className={"mt-3"}>
        {newName === null ? (
          <button
            type={"button"}
            className={GHOST_BUTTON}
            onClick={() => setNewName("")}>
            <LuFolderPlus size={15} />
            New folder {chosen ? `in “${chosen.name}”` : "at the top level"}
          </button>
        ) : (
          <div className={"flex gap-2"}>
            <input
              id={"picker-new-folder"}
              autoFocus={true}
              className={INPUT}
              placeholder={"Folder name"}
              value={newName}
              maxLength={100}
              onChange={(event) => setNewName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") create();
                if (event.key === "Escape") {
                  event.stopPropagation();
                  setNewName(null);
                }
              }}
            />
            <button
              type={"button"}
              className={PRIMARY_BUTTON}
              disabled={creating || !newName.trim()}
              onClick={create}>
              Add
            </button>
          </div>
        )}
      </div>
      <ErrorText>{createError || error}</ErrorText>
    </FilesModal>
  );
};
